import { useEffect, useState } from 'react';
import { Download, Share, SquarePlus, X } from 'lucide-react';
import {
  getPwaState,
  isIos,
  isNativeApp,
  isStandalone,
  promptInstall,
  subscribe,
} from '../utils/pwa';
import './InstallApp.css';

function usePwaState() {
  const [state, setState] = useState(getPwaState);
  useEffect(() => subscribe(() => setState(getPwaState())), []);
  return state;
}

/**
 * "Install app" button. Uses the native prompt on Chrome/Edge/Android;
 * on iOS Safari (no prompt API) it shows Add-to-Home-Screen steps instead.
 * Renders nothing when already installed, inside the Android app, or unsupported.
 */
export function InstallAppButton({ className = '' }) {
  const { canPrompt } = usePwaState();
  const [showIosHelp, setShowIosHelp] = useState(false);

  if (isNativeApp() || isStandalone()) return null;
  const ios = isIos();
  if (!canPrompt && !ios) return null;

  const onClick = () => {
    if (canPrompt) promptInstall();
    else setShowIosHelp(true);
  };

  return (
    <>
      <button type="button" className={`pwa-install-btn ${className}`} onClick={onClick}>
        <Download size={18} aria-hidden />
        Install app
      </button>

      {showIosHelp ? (
        <div
          className="pwa-sheet-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowIosHelp(false);
          }}
        >
          <div className="pwa-sheet" role="dialog" aria-modal="true" aria-labelledby="pwa-ios-title">
            <button
              type="button"
              className="pwa-sheet-close"
              onClick={() => setShowIosHelp(false)}
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h2 id="pwa-ios-title">Install Sangram Sena</h2>
            <ol>
              <li>
                Tap the <Share size={16} aria-label="Share" /> <strong>Share</strong> button in Safari’s
                toolbar.
              </li>
              <li>
                Choose <SquarePlus size={16} aria-hidden /> <strong>Add to Home Screen</strong>.
              </li>
              <li>
                Tap <strong>Add</strong>. The app opens full-screen from your home screen.
              </li>
            </ol>
            <p className="pwa-sheet-note">On iPhone this only works in Safari.</p>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Global banner shown when a new deployed version is ready. */
export function UpdateBanner({ onReload }) {
  const { updateReady } = usePwaState();
  if (!updateReady) return null;
  return (
    <div className="pwa-update" role="status">
      <span>A new version of the app is available.</span>
      <button type="button" onClick={onReload}>
        Refresh
      </button>
    </div>
  );
}
